import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  type Query
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  AppConfig,
  CompetitionStandings,
  CyclingStandings,
  Entry,
  Fixture,
  Group,
  League,
  Membership,
  Round,
  TeamSquad,
  User
} from '@h2h/shared';
import { db } from '../../lib/firebase';
import { FIVE_MIN, useLiveDoc, useLiveQuerySnap } from '../firestore/live';

/**
 * The app-wide config doc (`config/app`) — the force-update gate and any future
 * app-global flags. World-readable, so this works before sign-in. Live, so
 * flipping the gate from the admin dashboard reaches even already-open apps.
 */
export function useAppConfig() {
  return useLiveDoc<AppConfig>(['appConfig'], doc(db, 'config', 'app'), true);
}

/** All configured leagues (public read). */
export function useLeagues() {
  return useQuery({
    queryKey: ['leagues'],
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<League[]> => {
      const snap = await getDocs(collection(db, 'leagues'));
      return snap.docs.map((d) => d.data() as League);
    }
  });
}

/** The signed-in user's profile doc. */
export function useProfile(uid: string | null) {
  return useQuery({
    queryKey: ['user', uid],
    enabled: !!uid,
    queryFn: async (): Promise<User | null> => {
      const snap = await getDoc(doc(db, 'users', uid!));
      return snap.exists() ? (snap.data() as User) : null;
    }
  });
}

/** Every league membership for a user (their joined leagues + per-league tier/history). */
export function useMyMemberships(uid: string | null) {
  return useQuery({
    queryKey: ['memberships', uid],
    enabled: !!uid,
    queryFn: async (): Promise<Membership[]> => {
      const q = query(collection(db, 'memberships'), where('userId', '==', uid));
      const snap = await getDocs(q);
      return snap.docs.map((d) => d.data() as Membership);
    }
  });
}

/** The user's membership in a single league (tier + vanity history). */
export function useMyMembership(leagueId: string | undefined, uid: string | null) {
  return useQuery({
    queryKey: ['membership', leagueId, uid],
    enabled: !!leagueId && !!uid,
    queryFn: async (): Promise<Membership | null> => {
      const snap = await getDoc(doc(db, 'memberships', `${uid}_${leagueId}`));
      return snap.exists() ? (snap.data() as Membership) : null;
    }
  });
}

/** Approximate number of players competing in a league (membership count). */
export function useLeagueMemberCount(leagueId: string | undefined) {
  return useQuery({
    queryKey: ['memberCount', leagueId],
    enabled: !!leagueId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<number> => {
      const q = query(collection(db, 'memberships'), where('leagueId', '==', leagueId));
      const agg = await getCountFromServer(q);
      return agg.data().count;
    }
  });
}

/** The current (open/most-recent) round for a league. Live: status flips
 *  (OPEN → LOCKING → SETTLED) land on screen the instant the engine writes them. */
export function useCurrentRound(leagueId: string | undefined, roundId: string | null | undefined) {
  const enabled = !!leagueId && !!roundId;
  const ref = enabled ? doc(db, 'leagues', leagueId!, 'rounds', roundId!) : null;
  return useLiveDoc<Round>(['round', leagueId, roundId], ref, enabled);
}

/** Every round for a league (used for season history). */
export function useLeagueRounds(leagueId: string | undefined) {
  return useQuery({
    queryKey: ['rounds', leagueId],
    enabled: !!leagueId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<Round[]> => {
      const snap = await getDocs(collection(db, 'leagues', leagueId!, 'rounds'));
      return snap.docs.map((d) => d.data() as Round);
    }
  });
}

/** Real-competition standings (groups/table) for a league's Table tab. */
export function useCompetitionStandings(leagueId: string | undefined) {
  return useQuery({
    queryKey: ['standings', leagueId],
    enabled: !!leagueId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<CompetitionStandings | null> => {
      const snap = await getDoc(doc(db, 'leagues', leagueId!, 'competition', 'standings'));
      return snap.exists() ? (snap.data() as CompetitionStandings) : null;
    }
  });
}

/** Real race standings (GC + jerseys + the stage finish) for a cycling league's
 *  Table tab — the cycling counterpart of useCompetitionStandings. Live, so the
 *  table refreshes the moment the engine ingests a finished stage's PCS page. */
export function useCyclingStandings(leagueId: string | undefined) {
  const enabled = !!leagueId;
  const ref = enabled ? doc(db, 'leagues', leagueId!, 'competition', 'cycling') : null;
  return useLiveDoc<CyclingStandings>(['cyclingStandings', leagueId], ref, enabled);
}

/** All fixtures in a round (teams, kickoff, status, final result). When `live`,
 *  a real-time listener streams score/status/event changes as they happen (~1s);
 *  otherwise it's a one-shot read. */
export function useRoundFixtures(
  leagueId: string | undefined,
  roundId: string | null | undefined,
  live = false
) {
  const enabled = !!leagueId && !!roundId;
  const q: Query | null = enabled
    ? collection(db, 'leagues', leagueId!, 'rounds', roundId!, 'fixtures')
    : null;
  return useLiveQuerySnap<Fixture[]>(['fixtures', leagueId, roundId], q, enabled, live, (snap) =>
    snap.docs.map((d) => d.data() as Fixture)
  );
}

/**
 * Cached team squads (for the "anytime scorer" picker), keyed by teamId. Reads the
 * engine-ingested squad docs at leagues/{leagueId}/squads/{teamId} for the given
 * teams. Squads change rarely, so this is a one-shot read with a generous staleTime
 * — a team without a squad doc simply isn't in the map (its scorer market hides).
 */
export function useTeamSquads(leagueId: string | undefined, teamIds: (string | undefined | null)[]) {
  const ids = useMemo(
    () => [...new Set(teamIds.filter((id): id is string => !!id))].sort(),
    [teamIds]
  );
  return useQuery({
    queryKey: ['squads', leagueId, ids.join(',')],
    enabled: !!leagueId && ids.length > 0,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<Record<string, TeamSquad>> => {
      const snaps = await Promise.all(
        ids.map((teamId) => getDoc(doc(db, 'leagues', leagueId!, 'squads', teamId)))
      );
      const out: Record<string, TeamSquad> = {};
      snaps.forEach((s) => {
        if (s.exists()) out[s.id] = s.data() as TeamSquad;
      });
      return out;
    }
  });
}

/** The signed-in user's own entry (predictions) for a round. Owner-only read.
 *  Live: graded markets and the final round score appear the moment the engine
 *  settles, without needing a refresh. */
export function useMyEntry(
  leagueId: string | undefined,
  roundId: string | null | undefined,
  uid: string | null
) {
  const enabled = !!leagueId && !!roundId && !!uid;
  const ref = enabled ? doc(db, 'leagues', leagueId!, 'rounds', roundId!, 'entries', uid!) : null;
  return useLiveDoc<Entry>(['entry', leagueId, roundId, uid], ref, enabled);
}

/** The full group (public board) the user belongs to for a round. When `live`, a
 *  real-time listener streams every standings change as rivals' picks settle. */
export function useMyGroup(
  leagueId: string | undefined,
  roundId: string | null | undefined,
  uid: string | null,
  live = false
) {
  const enabled = !!leagueId && !!roundId && !!uid;
  const q: Query | null = enabled
    ? query(
        collection(db, 'leagues', leagueId!, 'rounds', roundId!, 'groups'),
        where('memberIds', 'array-contains', uid),
        limit(1)
      )
    : null;
  return useLiveQuerySnap<Group | null>(['group', leagueId, roundId, uid], q, enabled, live, (snap) =>
    snap.empty ? null : (snap.docs[0].data() as Group)
  );
}

export interface MyStanding {
  rankInGroup: number | null;
  divisionSize: number | null;
}

/** Where the user sits in their group for a round (rank + group size), if grouped. */
export function useMyStanding(
  leagueId: string | undefined,
  roundId: string | null | undefined,
  uid: string | null
) {
  const group = useMyGroup(leagueId, roundId, uid);
  const g = group.data;
  const row = g?.standings.find((r) => r.userId === uid);
  return {
    ...group,
    data: {
      rankInGroup: row?.rankInGroup ?? null,
      divisionSize: g?.memberIds.length ?? null
    } as MyStanding
  };
}

export interface SeasonRoundResult {
  round: Round;
  entry: Entry;
  /** Group size for that round — drives "2nd of 12" copy on season history rows. */
  divisionSize: number | null;
}

/** The user's own settled entries across every round in a league (season recap). */
export function useMySeasonResults(leagueId: string | undefined, uid: string | null) {
  return useQuery({
    queryKey: ['seasonResults', leagueId, uid],
    enabled: !!leagueId && !!uid,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<SeasonRoundResult[]> => {
      const roundsSnap = await getDocs(collection(db, 'leagues', leagueId!, 'rounds'));
      const rounds = roundsSnap.docs.map((d) => d.data() as Round);
      const results = await Promise.all(
        rounds.map(async (round) => {
          const entrySnap = await getDoc(
            doc(db, 'leagues', leagueId!, 'rounds', round.roundId, 'entries', uid!)
          );
          if (!entrySnap.exists()) return null;
          const entry = entrySnap.data() as Entry;
          let divisionSize: number | null = null;
          if (entry.groupId) {
            const groupSnap = await getDoc(
              doc(db, 'leagues', leagueId!, 'rounds', round.roundId, 'groups', entry.groupId)
            );
            if (groupSnap.exists()) {
              divisionSize = (groupSnap.data() as Group).memberIds.length;
            }
          }
          return { round, entry, divisionSize } as SeasonRoundResult;
        })
      );
      return results
        .filter((r): r is SeasonRoundResult => r != null && r.entry.roundScore != null)
        .sort((a, b) => b.round.firstKickoffAt - a.round.firstKickoffAt);
    }
  });
}

export interface FriendStanding {
  userId: string;
  displayName: string;
  photoURL?: string;
  tier: number;
  joinedAt: number;
  isYou: boolean;
}

/**
 * The user's friends who have joined this league, plus the user, ranked by tier.
 * Friends' predictions/entries are private (firestore.rules), so the only shared
 * competitive signal we can read is their public tier — never their scores.
 */
export function useFriendsInLeague(uid: string | null, leagueId: string | undefined) {
  return useQuery({
    queryKey: ['friendsInLeague', uid, leagueId],
    enabled: !!uid && !!leagueId,
    queryFn: async (): Promise<FriendStanding[]> => {
      const meSnap = await getDoc(doc(db, 'users', uid!));
      const me = meSnap.exists() ? (meSnap.data() as User) : null;
      const ids = [...new Set([uid!, ...(me?.friendIds ?? [])])];

      const rows = await Promise.all(
        ids.map(async (id) => {
          const [mSnap, uSnap] = await Promise.all([
            getDoc(doc(db, 'memberships', `${id}_${leagueId}`)),
            getDoc(doc(db, 'users', id))
          ]);
          if (!mSnap.exists()) return null; // not in this league
          const m = mSnap.data() as Membership;
          const u = uSnap.exists() ? (uSnap.data() as User) : null;
          return {
            userId: id,
            displayName: u?.displayName ?? 'Player',
            photoURL: u?.photoURL,
            tier: m.tier,
            joinedAt: m.joinedAt,
            isYou: id === uid
          } as FriendStanding;
        })
      );

      return rows
        .filter((r): r is FriendStanding => r != null)
        .sort((a, b) => b.tier - a.tier || a.joinedAt - b.joinedAt);
    }
  });
}

export interface LeagueSeasonRow {
  userId: string;
  displayName: string;
  avatarTone: number;
  photoURL?: string;
  tier: number;
  /** Lifetime sum of round scores (vanity). */
  seasonPoints: number;
  /** Settled rounds this member has played — the per-round average's denominator. */
  roundsPlayed: number;
  /** seasonPoints / roundsPlayed (0 when they've played none yet). */
  avgPerRound: number;
  isYou: boolean;
}

/**
 * Every member of a league with their vanity season stats — the league-wide
 * leaderboard. Season points are display-only and NEVER gate the real ladder (the
 * division ladder still resets every round, so a late joiner is never behind on
 * what matters); this is purely a "who's been sharp this season" lens, sortable by
 * all-time total or points-per-round. Reads every membership in the league
 * (firestore.rules allow any signed-in user to read memberships) and joins each
 * player's public profile for the name/avatar. Cached for a few minutes rather
 * than live — season totals only move when a round settles.
 */
export function useLeagueSeason(leagueId: string | undefined, uid: string | null) {
  return useQuery({
    queryKey: ['leagueSeason', leagueId, uid],
    enabled: !!leagueId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<LeagueSeasonRow[]> => {
      const mSnap = await getDocs(
        query(collection(db, 'memberships'), where('leagueId', '==', leagueId))
      );
      const members = mSnap.docs.map((d) => d.data() as Membership);
      const userSnaps = await Promise.all(members.map((m) => getDoc(doc(db, 'users', m.userId))));
      const byId = new Map(
        userSnaps.filter((s) => s.exists()).map((s) => [s.id, s.data() as User])
      );
      return members.map((m) => {
        const u = byId.get(m.userId);
        const seasonPoints = m.seasonPoints ?? 0;
        const roundsPlayed = m.roundsPlayed ?? 0;
        return {
          userId: m.userId,
          displayName: u?.displayName ?? 'Player',
          avatarTone: u?.avatarTone ?? 0,
          photoURL: u?.photoURL,
          tier: m.tier,
          seasonPoints,
          roundsPlayed,
          avgPerRound: roundsPlayed > 0 ? seasonPoints / roundsPlayed : 0,
          isYou: m.userId === uid
        } as LeagueSeasonRow;
      });
    }
  });
}
